import type { HermesMessage, HermesSkillId } from "@/lib/hermes-api";
import {
  buildKbAdaptiveSuggestions,
  type KnowledgeEntryChip,
  type KnowledgeEntryFieldMeta,
} from "@/lib/hermes-knowledge-entry";
import { kbInboxPath } from "@/lib/kb-routes";

/** Alias kept for Hermes drawer / empty-state chips. */
export type ConversationSuggestion = KnowledgeEntryChip;

/**
 * Enterprise KB snapshot for empty-conversation suggestions (M3.5b spirit).
 * Loaded client-side from wikis/pages/raw_sources when kb skill is selected.
 */
export type KbSuggestionSnapshot = {
  accountId: string;
  brandId?: string | null;
  companyProfile?: {
    pageId: number;
    wikiId: number;
    pageSlug: string;
    fields: KnowledgeEntryFieldMeta[];
    structured: Record<string, unknown>;
  };
  /** Seed pages whose structured fields are all empty. */
  emptySeedTitles: string[];
  /** Pending / available inbox raw sources. */
  inboxCount: number;
};

/** Conversation is empty for suggestion purposes when no user/assistant turns yet. */
export function isConversationEmpty(messages: HermesMessage[]): boolean {
  return !messages.some((message) => message.role === "user" || message.role === "assistant");
}

export function buildFetchSkillSuggestions(): ConversationSuggestion[] {
  return [
    {
      id: "fetch-summarize-url",
      label: "抓取网址并摘要",
      prompt: "请抓取我提供的网址内容，并用要点列表总结关键信息。网址：",
    },
    {
      id: "fetch-compare-sources",
      label: "对比两篇公开资料",
      prompt: "请帮我抓取并对比以下公开资料的异同（我会补充链接或关键词）：",
    },
    {
      id: "fetch-company-news",
      label: "检索企业公开信息",
      prompt: "请联网检索并整理这家企业的公开信息（官网、简介、近期动态），标注来源。企业名：",
    },
  ];
}

function buildGenericKbStarters(accountId?: string): ConversationSuggestion[] {
  const chips: ConversationSuggestion[] = [
    {
      id: "kb-generic-intro",
      label: "帮我整理企业基本信息",
      prompt: "帮我录入企业画像要点。请先用访谈方式问我公司全称、简称和主营业务。",
    },
    {
      id: "kb-generic-business",
      label: "帮我补充公司主营业务",
      prompt: "帮我补充公司主营业务。请以访谈方式问我：公司做什么、服务谁、核心产品或服务是什么？",
    },
  ];

  if (accountId) {
    chips.push({
      id: "kb-generic-inbox",
      label: "去知识旅程上传素材",
      href: kbInboxPath({ accountId, brandId: null }),
    });
  }

  return chips;
}

function buildKbEmptySuggestions(snapshot: KbSuggestionSnapshot): ConversationSuggestion[] {
  const { companyProfile, inboxCount, emptySeedTitles, accountId, brandId } = snapshot;

  if (inboxCount > 0) {
    const seedHint =
      emptySeedTitles.length > 0
        ? `当前空种子条目：${emptySeedTitles.slice(0, 3).join("、")}。`
        : "";
    return [
      {
        id: "use-inbox-materials",
        label: `根据旅程素材整理（${inboxCount}）`,
        prompt: `知识旅程里有 ${inboxCount} 条素材。${seedHint}请先读取/概括这些素材，再访谈补齐企业画像缺口，需要写入时用提案让我确认。`,
      },
      {
        id: "open-inbox-materials",
        label: "打开知识旅程",
        href: kbInboxPath({ accountId, brandId }),
      },
      ...(companyProfile
        ? buildKbAdaptiveSuggestions({
            accountId,
            brandId,
            fields: companyProfile.fields,
            structured: companyProfile.structured,
          }).filter((chip) => chip.id !== "open-inbox" && !chip.id.startsWith("open-inbox-"))
        : []),
    ].slice(0, 5);
  }

  if (companyProfile) {
    return buildKbAdaptiveSuggestions({
      accountId,
      brandId,
      fields: companyProfile.fields,
      structured: companyProfile.structured,
    });
  }

  if (emptySeedTitles.length > 0) {
    return [
      {
        id: "kb-empty-seeds",
        label: "填写空的种子条目",
        prompt: `知识库还有未填写的种子条目（${emptySeedTitles.slice(0, 3).join("、")}）。请用访谈方式帮我逐项补齐。`,
      },
      ...buildGenericKbStarters(accountId),
    ].slice(0, 4);
  }

  return buildGenericKbStarters(accountId);
}

/**
 * Empty-state conversation suggestions when ≥1 skill is selected and chat has no turns.
 * KB suggestions are semi-automatic from enterprise fill / inbox / empty seeds (M3.5b).
 */
export function buildEmptyConversationSuggestions(opts: {
  skills: HermesSkillId[];
  kbSnapshot?: KbSuggestionSnapshot | null;
}): ConversationSuggestion[] {
  const { skills, kbSnapshot } = opts;
  if (skills.length === 0) return [];

  const chips: ConversationSuggestion[] = [];

  if (skills.includes("kb")) {
    chips.push(
      ...(kbSnapshot ? buildKbEmptySuggestions(kbSnapshot) : buildGenericKbStarters(kbSnapshot?.accountId)),
    );
  }

  if (skills.includes("fetch")) {
    const fetchChips = buildFetchSkillSuggestions();
    if (skills.includes("kb")) {
      // Keep UI compact: one fetch starter when combined with kb adaptive chips.
      chips.push(fetchChips[0]!);
    } else {
      chips.push(...fetchChips);
    }
  }

  // Dedupe by id while preserving order.
  const seen = new Set<string>();
  return chips.filter((chip) => {
    if (seen.has(chip.id)) return false;
    seen.add(chip.id);
    return true;
  });
}
